import { Alert, AlertDescription, AlertTitle, Button, Card } from '@design-system';
import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { OnboardingInsightsSummary } from '@/pages/OnboardingInsightsSummary';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';
import { D2CCheckoutPage } from '@/pages/D2CCheckoutPage';
import { OnboardingD2CVirtualMeeting } from '@/pages/OnboardingD2CVirtualMeeting';
import { useNavigateToPage } from '@/Router';
import { selectIntakeSurvey, selectSession } from '@/model';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useIsConsumerFlowEnabled } from '@/hooks/use-app-mode';
import { consumerEntryPath } from '@/features/consumer';
import { Star, Eye, Heart, TrendingUp, Info } from 'lucide-react';
import { isAgeInRange, QUOTE_AGE_RANGE } from '@shared';
import { calcClientDemographics } from '@shared/calculations/demographics';
import { analytics } from '@shared/posthog/analytics';

const NYTLogo = () => (
  <img
    src="/logos/nytimes.svg"
    alt="The New York Times"
    className="h-8"
  />
);

const VALUE_PROPS = [
  {
    icon: <Eye className="text-primary size-5" />,
    title: 'Understand what care may cost',
    description: 'Fit your plan to your needs',
  },
  {
    icon: <Heart className="text-primary size-5" />,
    title: 'How to reduce stress on loved ones',
    description: 'Comprehensive care planning and family involvement predictions',
  },
  {
    icon: <TrendingUp className="text-primary size-5" />,
    title: 'Access funding strategies',
    description: 'Analyze any assets, income, or insurance policy and model how it fits your plan',
  },
];

// Wherever the guided consumer flow is enabled, it takes the paywall's place.
export function OnboardingD2CPaywall({ clientId }: ClientIdProps) {
  if (useIsConsumerFlowEnabled()) return <Navigate to={consumerEntryPath(clientId)} replace />;
  return <D2CPaywall clientId={clientId} />;
}

function D2CPaywall({ clientId }: ClientIdProps) {
  const navigateToPage = useNavigateToPage();
  const { clientStripe } = useSelector(selectSession);
  const intakeSurvey = useSelector(selectIntakeSurvey);

  const hasActivePlan = clientStripe?.hasActivePlan ?? false;

  const { primaryAge } = calcClientDemographics(intakeSurvey ?? {});
  const isAgeIneligible = primaryAge > 0 && !isAgeInRange(primaryAge);

  useSlideSetup({
    slidePage: OnboardingD2CPaywall,
    nextButtonLabel: 'Next',
    nextSlidePage: hasActivePlan ? OnboardingCostFactors : undefined,
    previousSlidePage: OnboardingInsightsSummary,
    shouldHideNextNavigation: !hasActivePlan,
  });

  const handleAccessClick = () => {
    analytics.d2c.accessClicked({ clientId });
    navigateToPage(D2CCheckoutPage, { clientId });
  };

  const handleVirtualMeetingClick = () => {
    analytics.d2c.virtualMeetingClicked({ clientId });
    navigateToPage(OnboardingD2CVirtualMeeting, { clientId });
  };

  return (
    <OnboardingLayout>
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center py-6 md:pb-16">
        <h1 className="text-balance text-center text-2xl font-bold text-gray-950 lg:text-4xl">
          Turn your predictions into a real long-term care plan
        </h1>
        <p className="text-foreground mt-3 text-balance text-center text-base lg:text-lg">
          See what your care will likely cost, build a realistic care plan, and compare ways to fund
          it.
        </p>

        {isAgeIneligible && (
          <Alert variant="warning" className='mt-6'>
            <Info />
              <AlertTitle>
                Insurance quotes aren&apos;t available for your age
              </AlertTitle>
              <AlertDescription>
                Our system currently offer policies to people ages {QUOTE_AGE_RANGE.min}
                -{QUOTE_AGE_RANGE.max}, so we won&apos;t be able to show you policy quotes to
                compare or purchase. You can still use Waterlily to build a self-funding plan,
                modeling your future care costs against your own assets and income, but you
                won&apos;t be able to shop for insurance through us. Continue only if a self-funding
                plan is what you&apos;re looking for.
              </AlertDescription>
          </Alert>
        )}

        <div className="grid-col-1 mt-8 grid w-full gap-8 lg:grid-cols-2">
          <div className="row-start-2 lg:row-start-1">
            <h2 className="text-xl font-bold text-gray-950 md:text-2xl">
              Why families go deeper with Waterlily
            </h2>
            <div className="mt-6 space-y-6">
              {VALUE_PROPS.map(prop => (
                <div
                  key={prop.title}
                  className="flex gap-3"
                >
                  <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                    {prop.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-950">{prop.title}</h3>
                    <p className="text-foreground mt-0.5 text-sm">{prop.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Card className="row-start-1 flex flex-col gap-4 p-6">
            <div className="rounded-lg bg-green-50 px-3 py-2 text-center">
              <p className="text-sm font-semibold text-green-800">🎉 Limited time only — 50% off</p>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">As featured in</p>
              <NYTLogo />
            </div>

            <h3 className="text-xl font-bold text-gray-950">Finish your LTC plan</h3>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="size-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                Trusted by thousands of families and financial professionals
              </p>
              <p className="text-foreground mt-3 text-sm italic leading-relaxed">
                &ldquo;We highly recommend their services for planning your long term health care
                needs. Thank you Lily and Waterlily staff for your time and sincere commitment in
                helping families like ours.&rdquo;
              </p>
              <p className="mt-2 text-sm font-medium text-gray-950">-- George Tabora</p>
            </div>

            <Button
              size="lg"
              className="mt-2 w-full rounded-full"
              onClick={handleAccessClick}
            >
              Access Now — $9.99
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full rounded-full border-gray-400"
              onClick={handleVirtualMeetingClick}
            >
              Access via Virtual Meeting — Free
            </Button>
            <p className="text-foreground text-center text-xs">
              <span className="text-gray-400 line-through">$19.99</span>{' '}
              <span className="font-semibold text-gray-950">$9.99</span> one-time payment for
              lifetime access. No recurring charges.
            </p>
          </Card>
        </div>
      </div>
    </OnboardingLayout>
  );
}
